import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/*
 * Scroll manager.
 *
 * - New page (link click / navigate): jump to the top.
 * - Back / Forward, or navigate(url, { state: { restoreScroll: true } }):
 *   return to where the visitor was on that page.
 * - Only the query string changed (filters, sort): leave the scroll alone.
 *
 * Positions live in sessionStorage so they survive within the tab session.
 */

const STORE_KEY = "sa:scroll";
const LIST_KEY = "sa:lists";
const MAX_ENTRIES = 60;

const readJSON = (key) => {
  try {
    return JSON.parse(sessionStorage.getItem(key) || "{}");
  } catch {
    return {};
  }
};

const writeJSON = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable (private mode) - restoring just won't happen */
  }
};

const urlKey = (loc) => `url:${loc.pathname}${loc.search}`;

/** Remember the last URL (with filters) of a list page, e.g. "shop" or "admin-products". */
export const rememberListUrl = (name, url) => {
  const lists = readJSON(LIST_KEY);
  if (lists[name] !== url) {
    lists[name] = url;
    writeJSON(LIST_KEY, lists);
  }
};

export const getListUrl = (name, fallback) => readJSON(LIST_KEY)[name] || fallback;

const savePosition = (keys, y) => {
  const map = readJSON(STORE_KEY);
  keys.forEach((k) => {
    map[k] = y;
  });
  const names = Object.keys(map);
  if (names.length > MAX_ENTRIES) {
    names.slice(0, names.length - MAX_ENTRIES).forEach((n) => delete map[n]);
  }
  writeJSON(STORE_KEY, map);
};

/**
 * Scroll to `target` once the page is tall enough (content may still be loading),
 * retrying for a short while. Stops early if the visitor starts scrolling.
 */
const restoreScroll = (target) => {
  let frames = 0;
  let settled = 0;
  let cancelled = false;
  let timer;
  const cancel = () => {
    cancelled = true;
  };
  const events = ["wheel", "touchstart", "keydown", "mousedown"];
  events.forEach((e) => window.addEventListener(e, cancel, { passive: true, once: true }));

  const tick = () => {
    if (cancelled) return;
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    if (maxScroll >= target - 2) {
      window.scrollTo({ top: target, left: 0, behavior: "instant" });
      // Stay on target for a few frames: images above can still shift the layout.
      settled += 1;
      if (settled > 20) {
        events.forEach((e) => window.removeEventListener(e, cancel));
        return;
      }
    } else {
      settled = 0;
    }
    frames += 1;
    if (frames < 240) {
      timer = setTimeout(tick, 16);
    } else {
      events.forEach((e) => window.removeEventListener(e, cancel));
    }
  };
  // Timers (not requestAnimationFrame) so this also works in background / non-rendering tabs
  timer = setTimeout(tick, 0);
  return () => {
    cancel();
    clearTimeout(timer);
  };
};

const ScrollToTop = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const keysRef = useRef([location.key, urlKey(location)]);
  const prevPathRef = useRef(location.pathname);
  const cancelRef = useRef(null);

  // We manage restoration ourselves; the browser's own would fight the lazy-loaded pages.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Record the scroll position of the page being viewed.
  useEffect(() => {
    let timer = null;
    const onScroll = () => {
      if (timer) return;
      // Throttled save (timer, not requestAnimationFrame, so it also runs in background tabs)
      timer = setTimeout(() => {
        timer = null;
        savePosition(keysRef.current, Math.round(window.scrollY));
      }, 100);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
    };
  }, []);

  // Layout effect: switch the "current page" keys before the new page can fire scroll events.
  useLayoutEffect(() => {
    const samePath = prevPathRef.current === location.pathname;
    prevPathRef.current = location.pathname;
    keysRef.current = [location.key, urlKey(location)];

    // Same page, address merely rewritten (filters / sort / hash, or a page re-syncing its own
    // query string with replace): leave the scroll position AND any restore in progress alone.
    if (samePath && navigationType !== "POP") return;

    if (cancelRef.current) {
      cancelRef.current();
      cancelRef.current = null;
    }

    const wantsRestore = navigationType === "POP" || location.state?.restoreScroll;
    if (wantsRestore) {
      const map = readJSON(STORE_KEY);
      const target = map[location.key] ?? map[urlKey(location)];
      if (target > 0) {
        cancelRef.current = restoreScroll(target);
        return;
      }
    }

    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location, navigationType]);

  return null;
};

export default ScrollToTop;
